// ── MatchReceiptsModal — OCR 영수증 ↔ 홈택스 카드 매칭 ──
// 자동 매칭된 페어를 사용자가 검토하고 일괄 적용

import React, { useState, useEffect, useMemo } from 'react';
import { findMatches, applyMatches } from '../utils/transactionMatcher';
import { L, M } from '../i18n/L';   // V160 중국어

const fmt = n => Math.round(Number(n) || 0).toLocaleString('ko-KR');

export default function MatchReceiptsModal({ isOpen, onClose, user, transactions = [] }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);

  const matches = useMemo(() => isOpen ? findMatches(transactions) : [], [isOpen, transactions]);

  // 모달 열릴 때 모든 매칭 자동 선택
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set(matches.map(m => m.cardTx.id)));
      setApplying(false);
      setDone(null);
    }
  }, [isOpen, matches]);

  if (!isOpen) return null;

  const toggle = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedMatches = matches.filter(m => selectedIds.has(m.cardTx.id));

  const handleApply = async () => {
    if (selectedMatches.length === 0) {
      alert(L("적용할 매칭이 없습니다."));
      return;
    }
    if (!user?.uid) {
      alert(L("로그인 정보가 없습니다."));
      return;
    }
    setApplying(true);
    try {
      const result = await applyMatches(user.uid, selectedMatches);
      setDone(result);
    } catch (e) {
      console.error(e);
      alert(L("매칭 적용 실패: {0}", e.message));
    } finally {
      setApplying(false);
    }
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, padding: 12,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 14, width: 1000, maxWidth: '100%',
        maxHeight: '94vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 10px 50px rgba(0,0,0,0.25)',
      }}>
        {/* 헤더 */}
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #eee',
                      display: 'flex', alignItems: 'center', gap: 14, background: '#f9fafb' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 17, fontWeight: 800 }}>{L("🔗 영수증·카드 매칭")}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
              {L("OCR 영수증과 홈택스 카드 거래가 같은 거래로 추정되면 영수증 사진을 카드 거래에 인계 + 영수증 거래 삭제")}
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: 'transparent', fontSize: 22,
            color: '#9ca3af', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* 본문 */}
        <div style={{ padding: 18, overflow: 'auto', flex: 1, background: '#fafafa' }}>
          {done ? (
            <div style={{
              padding: 30, textAlign: 'center', background: '#fff',
              borderRadius: 10, border: '1px solid #FED7AA',
            }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: '#EA580C', marginBottom: 8 }}>
                {L("매칭 완료")}
              </div>
              <div style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.7 }}>
                {L("✓ {0}건 매칭 적용 (영수증 사진 인계 + 중복 거래 삭제)", done.applied)}
                {done.skipped > 0 && (
                  <><br/>{L("⚠ {0}건 실패", done.skipped)}</>
                )}
              </div>
              <button onClick={onClose} style={{
                marginTop: 16, padding: '10px 22px', borderRadius: 8, border: 'none',
                background: '#FF6F0F', color: '#fff', fontSize: 13, fontWeight: 800, cursor: 'pointer',
              }}>{L("닫기")}</button>
            </div>
          ) : matches.length === 0 ? (
            <div style={{
              padding: 30, textAlign: 'center', background: '#fff',
              borderRadius: 10, border: '1px solid #e5e7eb',
            }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🔍</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#374151' }}>
                {L("매칭 가능한 거래가 없어요")}
              </div>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6 }}>
                {L("OCR 영수증과 홈택스 카드 거래가 같은 날짜·금액·공급처여야 매칭됩니다.")}<br/>
                {L("먼저 영수증 OCR + 홈택스 엑셀 둘 다 등록한 뒤 다시 시도해보세요.")}
              </div>
            </div>
          ) : (
            <>
              <div style={{
                padding: 10, marginBottom: 12, background: '#eff6ff',
                borderRadius: 8, border: '1px solid #93c5fd',
                fontSize: 12, color: '#0369a1', lineHeight: 1.6,
              }}>
                {L("📋 매칭 후보")} <strong>{L("{0}개", matches.length)}</strong> {L("발견 — 체크된 페어만 적용됩니다.")}
                <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>
                  {L("적용 시: 카드 거래에 영수증 사진·품목 정보 인계 + 중복된 영수증 거래 삭제 (되돌리기 불가)")}
                </div>
              </div>
              <div style={{
                background: '#fff', borderRadius: 10,
                border: '1px solid #e5e7eb', overflow: 'hidden',
              }}>
                {matches.map((m, idx) => (
                  <MatchRow key={m.cardTx.id} match={m}
                    selected={selectedIds.has(m.cardTx.id)}
                    onToggle={() => toggle(m.cardTx.id)} />
                ))}
              </div>
            </>
          )}
        </div>

        {/* 푸터 */}
        {!done && (
          <div style={{ padding: '12px 20px', borderTop: '1px solid #eee',
                        display: 'flex', gap: 10, justifyContent: 'space-between',
                        alignItems: 'center', background: '#fff' }}>
            <div style={{ fontSize: 12, color: '#6b7280' }}>
              {matches.length === 0
                ? ''
                : L("✓ {0} / {1}건 선택됨", selectedMatches.length, matches.length)}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} disabled={applying} style={{
                padding: '10px 20px', borderRadius: 8, border: '1px solid #ddd',
                background: '#fff', color: '#555', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{L("취소")}</button>
              {matches.length > 0 && (
                <button onClick={handleApply} disabled={applying || selectedMatches.length === 0}
                  style={{
                    padding: '10px 22px', borderRadius: 8, border: 'none',
                    background: selectedMatches.length === 0 ? '#9ca3af' : '#FF6F0F',
                    color: '#fff', fontSize: 13, fontWeight: 800,
                    cursor: selectedMatches.length === 0 ? 'not-allowed' : 'pointer',
                  }}>
                  {applying ? L("적용 중...") : L("🔗 {0}건 매칭 적용", selectedMatches.length)}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MatchRow({ match, selected, onToggle }) {
  const ocr = match.ocrTx;
  const card = match.cardTx;
  const ocrAmt = (Number(ocr.supply) || 0) + (Number(ocr.vat) || 0);
  const cardAmt = (Number(card.supply) || 0) + (Number(card.vat) || 0);
  const scoreColor = match.score >= 1 ? '#EA580C' : match.score >= 0.85 ? '#0369a1' : '#52525B';

  return (
    <div style={{
      padding: '12px 14px', borderBottom: '1px solid #f3f4f6',
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: selected ? '#fff' : '#f9fafb',
      opacity: selected ? 1 : 0.6,
    }}>
      <input type="checkbox" checked={selected} onChange={onToggle}
        style={{ width: 16, height: 16, cursor: 'pointer', marginTop: 4, flexShrink: 0 }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        {/* 매칭 신뢰도 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{
            fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4,
            background: scoreColor + '22', color: scoreColor,
          }}>
            {L("매칭 신뢰도 {0}%", Math.round(match.score * 100))}
          </span>
          <span style={{ fontSize: 11, color: '#6b7280' }}>{match.reason}</span>
        </div>

        {/* 두 거래 비교 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 10, alignItems: 'center' }}>
          <TxCard label={L("📸 OCR 영수증")} tx={ocr} amount={ocrAmt} color="#EA580C"
            hasImage={!!ocr.receiptImageUrl} />
          <div style={{ fontSize: 18, color: '#9ca3af', textAlign: 'center' }}>↔</div>
          <TxCard label={L("💳 카드 거래")} tx={card} amount={cardAmt} color="#FF6F0F"
            isTarget />
        </div>
      </div>
    </div>
  );
}

function TxCard({ label, tx, amount, color, hasImage, isTarget }) {
  return (
    <div style={{
      padding: '8px 10px', borderRadius: 7,
      background: '#fff', border: `1px solid ${color}33`,
      fontSize: 11,
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, color, marginBottom: 3 }}>
        {label}
        {isTarget && <span style={{ marginLeft: 6, color: '#9ca3af', fontWeight: 500 }}>{L("(여기로 인계)")}</span>}
        {hasImage && <span style={{ marginLeft: 6 }}>📷</span>}
      </div>
      <div style={{ fontWeight: 700, color: '#1a1a1a',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {tx.partnerName || L("거래처 없음")}
      </div>
      <div style={{ color: '#6b7280', marginTop: 1 }}>
        {tx.date} · <strong style={{ color }}>{M(amount)}</strong>
        {tx.partnerBizNumber && ` · ${tx.partnerBizNumber}`}
      </div>
    </div>
  );
}
